import type { InventoryItem, InventoryBuyer, SimilarProductBuyers, FlaggedItem } from '../../types/inventory';

export interface EmailTemplateOutput {
    subject: string;
    /** Editable plain-text intro — shown in the textarea */
    plainIntro: string;
    /** Builds the inner body HTML (no <html>/<body> wrapper). Used for in-page preview via dangerouslySetInnerHTML. */
    buildBodyContent: (editedIntro: string) => string;
    /** Builds the full HTML document — used when sending via Graph API. */
    buildHtmlBody: (editedIntro: string) => string;
}

// --- Slow/No Mover single item ---

export function buildSlowMoverTemplate(
    item: InventoryItem,
    buyers: InventoryBuyer[],
    similarProducts: SimilarProductBuyers[],
    senderEmail: string,
): EmailTemplateOutput {
    const subject = `Slow Mover — Action needed: ${item.ProductName} (${item.CompanyCode})`;

    const plainIntro = [
        `Dear,`,
        ``,
        `please check the possibility of selling the following slow/no mover item to the customers listed here who have purchased the product in the past. Looking forward to your prompt feedback. Thanks.`,
    ].join('\n');

    const dataBlock = renderSingleItemTable(item);
    const buyersBlock = renderBuyersTable(buyers);
    const similarBlock = renderSimilarProductBuyers(similarProducts);
    const footer = renderFooter(senderEmail);

    const buildBodyContent = (editedIntro: string) => [
        plainTextToHtml(editedIntro),
        dataBlock,
        buyersBlock,
        similarBlock,
        footer,
    ].filter(Boolean).join('\n');

    return {
        subject,
        plainIntro,
        buildBodyContent,
        buildHtmlBody: (editedIntro: string) => wrapHtml(buildBodyContent(editedIntro)),
    };
}

// --- Destroy list ---

export function buildDestroyListTemplate(
    items: FlaggedItem[],
    senderEmail: string,
): EmailTemplateOutput {
    const totalValue = items.reduce((sum, i) => sum + (i.StockValueEUR ?? 0), 0);
    const subject = `Items to be destroyed — ${items.length} item${items.length === 1 ? '' : 's'}, total €${formatNumber(totalValue)}`;

    const plainIntro = [
        `Hi,`,
        ``,
        `Attached below is the current list of items flagged for destruction, with a total stock value of €${formatNumber(totalValue)}.`,
        ``,
        `Please review and confirm the next steps.`,
        ``,
        `Thank you.`,
    ].join('\n');

    const dataBlock = renderDestroyTable(items, totalValue);
    const footer = renderFooter(senderEmail);

    const buildBodyContent = (editedIntro: string) => [
        plainTextToHtml(editedIntro),
        dataBlock,
        footer,
    ].join('\n');

    return {
        subject,
        plainIntro,
        buildBodyContent,
        buildHtmlBody: (editedIntro: string) => wrapHtml(buildBodyContent(editedIntro)),
    };
}

// --- Shared HTML helpers ---

function wrapHtml(innerHtml: string): string {
    return [
        '<!DOCTYPE html>',
        '<html><head><meta charset="UTF-8"></head>',
        '<body style="font-family: Arial, Helvetica, sans-serif; font-size: 14px; color: #1f2937; line-height: 1.5;">',
        innerHtml,
        '</body></html>',
    ].join('\n');
}

function plainTextToHtml(text: string): string {
    const escaped = escapeHtml(text).trimEnd();
    const paragraphs = escaped
        .split(/\n{2,}/)
        .map(p => `<p style="margin: 0 0 12px 0;">${p.replace(/\n/g, '<br>')}</p>`);
    return paragraphs.join('\n');
}

function renderSingleItemTable(item: InventoryItem): string {
    const rows: [string, string][] = [
        ['Product', `${escapeHtml(item.ProductName)} (${escapeHtml(item.ProductCode)})`],
        ['Company', escapeHtml(item.CompanyCode)],
        ['Supplier', escapeHtml(item.SupplierName || item.SupplierCode || '—')],
        ['Warehouse', escapeHtml(`${item.WarehouseName || ''} ${item.WarehouseCountry ? `(${item.WarehouseCountry})` : ''}`.trim() || '—')],
        ['Inventory class', escapeHtml(`${item.InventoryClass} — ${item.ClassName}`)],
        ['Stock quantity', formatNumber(item.StockQuantity)],
        ['Stock value (EUR)', `€${formatNumber(item.StockValueEUR)}`],
        ['Price / kg (EUR)', item.StockQuantity ? `€${formatPricePerKg(item.StockValueEUR / item.StockQuantity)}` : '—'],
        ['Latest sale', formatDate(item.LatestSalesDate)],
        ['Days on hand', item.DaysOnHand != null ? String(item.DaysOnHand) : '—'],
    ];

    const rowsHtml = rows.map(([label, value]) => `
        <tr>
            <td style="padding: 6px 12px 6px 0; color: #6b7280; white-space: nowrap;">${label}</td>
            <td style="padding: 6px 0; color: #111827; font-weight: 500;">${value}</td>
        </tr>`).join('');

    return `
        <div style="margin: 16px 0; padding: 12px 16px; border: 1px solid #e5e7eb; border-radius: 6px; background: #f9fafb;">
            <div style="font-weight: 600; margin-bottom: 8px; color: #111827;">Item details</div>
            <table style="border-collapse: collapse; width: 100%;">
                ${rowsHtml}
            </table>
        </div>`;
}

function renderBuyersTable(buyers: InventoryBuyer[]): string {
    const title = `Past buyers (${buyers.length})`;

    if (!buyers || buyers.length === 0) {
        return `
        <div style="margin: 16px 0;">
            <div style="font-weight: 600; margin-bottom: 8px; color: #111827;">${title}</div>
            <div style="font-size: 12px; color: #6b7280; font-style: italic;">No past buyers found for this product.</div>
        </div>`;
    }

    const sorted = [...buyers].sort((a, b) => {
        const da = a.LastSaleDate ? new Date(a.LastSaleDate).getTime() : 0;
        const db = b.LastSaleDate ? new Date(b.LastSaleDate).getTime() : 0;
        return db - da;
    });

    const totalQty = sorted.reduce((s, b) => s + (b.TotalQuantity ?? 0), 0);
    const totalRev = sorted.reduce((s, b) => s + (b.RevenueAllTime ?? 0), 0);

    const rows = sorted.map(b => `
        <tr>
            <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${escapeHtml(b.CustomerName)}</td>
            <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${escapeHtml(b.SalesManagerName || '—')}</td>
            <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${escapeHtml(b.CountryISO3 || '—')}</td>
            <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${escapeHtml(formatDate(b.LastSaleDate))}</td>
            <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-align: right;">${formatNumber(b.TotalQuantity)}</td>
            <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-align: right;">€${formatNumber(b.RevenueAllTime)}</td>
        </tr>`).join('');

    return `
        <div style="margin: 16px 0;">
            <div style="font-weight: 600; margin-bottom: 8px; color: #111827;">${title}</div>
            <table style="border-collapse: collapse; width: 100%; border: 1px solid #e5e7eb;">
                <thead>
                    <tr style="background: #f3f4f6;">
                        <th style="padding: 6px 8px; text-align: left; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Customer</th>
                        <th style="padding: 6px 8px; text-align: left; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Sales Manager</th>
                        <th style="padding: 6px 8px; text-align: left; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Country</th>
                        <th style="padding: 6px 8px; text-align: left; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Last Sale</th>
                        <th style="padding: 6px 8px; text-align: right; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Qty</th>
                        <th style="padding: 6px 8px; text-align: right; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Revenue (EUR, all-time)</th>
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
                <tfoot>
                    <tr>
                        <td colspan="4" style="padding: 8px; text-align: right; font-weight: 600; font-size: 12px;">Total:</td>
                        <td style="padding: 8px; text-align: right; font-weight: 600; font-size: 12px;">${formatNumber(totalQty)}</td>
                        <td style="padding: 8px; text-align: right; font-weight: 600; font-size: 12px;">€${formatNumber(totalRev)}</td>
                    </tr>
                </tfoot>
            </table>
        </div>`;
}

function renderSimilarProductBuyers(similarProducts: SimilarProductBuyers[]): string {
    if (!similarProducts || similarProducts.length === 0) return '';

    const totalBuyers = similarProducts.reduce((s, p) => s + p.buyers.length, 0);
    if (totalBuyers === 0) return '';

    const groups = similarProducts
        .filter(p => p.buyers.length > 0)
        .map(p => {
            const sorted = [...p.buyers].sort((a, b) => {
                const da = a.LastSaleDate ? new Date(a.LastSaleDate).getTime() : 0;
                const db = b.LastSaleDate ? new Date(b.LastSaleDate).getTime() : 0;
                return db - da;
            });

            const rows = sorted.map(b => `
                <tr>
                    <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${escapeHtml(b.CustomerName)}</td>
                    <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${escapeHtml(b.SalesManagerName || '—')}</td>
                    <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${escapeHtml(b.CountryISO3 || '—')}</td>
                    <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${escapeHtml(formatDate(b.LastSaleDate))}</td>
                    <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-align: right;">${formatNumber(b.TotalQuantity)}</td>
                    <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-align: right;">€${formatNumber(b.RevenueAllTime)}</td>
                </tr>`).join('');

            return `
                <div style="margin-top: 12px;">
                    <div style="font-size: 13px; color: #374151; margin-bottom: 4px;">
                        <strong>${escapeHtml(p.productName)}</strong>
                        <span style="color: #6b7280;"> · ${escapeHtml(p.companyCode)} · ${p.buyers.length} buyer${p.buyers.length === 1 ? '' : 's'}</span>
                    </div>
                    <table style="border-collapse: collapse; width: 100%; border: 1px solid #e5e7eb;">
                        <thead>
                            <tr style="background: #f3f4f6;">
                                <th style="padding: 6px 8px; text-align: left; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Customer</th>
                                <th style="padding: 6px 8px; text-align: left; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Sales Manager</th>
                                <th style="padding: 6px 8px; text-align: left; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Country</th>
                                <th style="padding: 6px 8px; text-align: left; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Last Sale</th>
                                <th style="padding: 6px 8px; text-align: right; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Qty</th>
                                <th style="padding: 6px 8px; text-align: right; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Revenue (EUR, all-time)</th>
                            </tr>
                        </thead>
                        <tbody>${rows}</tbody>
                    </table>
                </div>`;
        }).join('');

    return `
        <div style="margin: 16px 0;">
            <div style="font-weight: 600; margin-bottom: 4px; color: #111827;">Buyers of similar products in other companies</div>
            <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px;">Matched by product-name pattern — potential additional customers.</div>
            ${groups}
        </div>`;
}

function formatDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    // Expect "YYYY-MM-DD" or ISO timestamp — keep only the date part.
    const d = String(iso).slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : String(iso);
}

function renderDestroyTable(items: FlaggedItem[], totalValue: number): string {
    const rows = items.map(i => `
        <tr>
            <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${escapeHtml(i.CompanyCode)}</td>
            <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${escapeHtml(i.ProductName)}<br><span style="color: #6b7280;">${escapeHtml(i.ProductCode)}</span></td>
            <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${escapeHtml(i.SupplierName || '—')}</td>
            <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${escapeHtml(`${i.WarehouseName || ''} ${i.WarehouseCountry ? `(${i.WarehouseCountry})` : ''}`.trim() || '—')}</td>
            <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-align: right;">${formatNumber(i.StockQuantity)}</td>
            <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px; text-align: right;">€${formatNumber(i.StockValueEUR)}</td>
            <td style="padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px;">${escapeHtml(i.Reason || '—')}</td>
        </tr>`).join('');

    return `
        <div style="margin: 16px 0;">
            <div style="font-weight: 600; margin-bottom: 8px; color: #111827;">Destroy list (${items.length} items)</div>
            <table style="border-collapse: collapse; width: 100%; border: 1px solid #e5e7eb;">
                <thead>
                    <tr style="background: #f3f4f6;">
                        <th style="padding: 6px 8px; text-align: left; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Co.</th>
                        <th style="padding: 6px 8px; text-align: left; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Product</th>
                        <th style="padding: 6px 8px; text-align: left; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Supplier</th>
                        <th style="padding: 6px 8px; text-align: left; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Warehouse</th>
                        <th style="padding: 6px 8px; text-align: right; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Qty</th>
                        <th style="padding: 6px 8px; text-align: right; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Value (EUR)</th>
                        <th style="padding: 6px 8px; text-align: left; font-size: 12px; color: #374151; border-bottom: 1px solid #e5e7eb;">Reason</th>
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
                <tfoot>
                    <tr>
                        <td colspan="5" style="padding: 8px; text-align: right; font-weight: 600; font-size: 12px;">Total:</td>
                        <td style="padding: 8px; text-align: right; font-weight: 600; font-size: 12px;">€${formatNumber(totalValue)}</td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>
        </div>`;
}

function renderFooter(senderEmail: string): string {
    return `
        <div style="margin-top: 24px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280;">
            Sent via the Dolder Reporting App by ${escapeHtml(senderEmail)}.
        </div>`;
}

function escapeHtml(s: string | null | undefined): string {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function formatNumber(n: number | null | undefined): string {
    if (n == null || Number.isNaN(n)) return '0';
    return Math.round(n).toLocaleString('en-US');
}

function formatPricePerKg(n: number | null | undefined): string {
    if (n == null || Number.isNaN(n)) return '—';
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
